import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from 'react'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * 共通 PasswordInput component (Feature 1)。
 *
 * 全 password input から型と eye-toggle UI のロジックを共通化:
 *   - input type=password / text の切替
 *   - `.input-group` + `.input-suffix` + `.icon-btn sm` を使用
 *     (theme.css 既存の position:relative 親 + absolute 右寄せ icon 構造)
 *   - aria-label を `i18n('common.password.show'|'hide')` で提供
 *   - input 側のプロパティ (id / name / value / onChange / autoComplete / required /
 *     disabled / maxLength / placeholder / inputMode) は全て props で透過
 *   - parent が `id` を指定しない場合は useId で自動採番
 *   - `forwardRef` で ref 受け渡し (LoginPage の emailInputRef 等のパターンに合わせる)
 *
 * Feature 6 (送信 Send page で eye icon が下にずれる) も本 component への置換で解消。
 * 旧 `.input-pw-row` / `.input-pw-toggle` 自作クラスは CSS が無く高さ不揃いだった。
 */

export interface PasswordInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> {
  /** input サイズ。`lg` でログイン / ロック画面の大型 input、未指定で通常 */
  size?: 'lg' | 'default'
  /** 表示状態を外部で制御したい場合 (例: 親 component で state を持つ場合)。
   *  未指定なら component 内部で state を持つ */
  visible?: boolean
  onVisibleChange?: (visible: boolean) => void
  /** eye アイコンの手前に追加で描画する trailing 要素 (例: 生成アイコン #5)。 */
  extraAction?: ReactNode
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput(
    { size = 'default', visible, onVisibleChange, extraAction, id: idProp, className, ...rest },
    ref,
  ): JSX.Element {
    const { t } = useTranslation()
    const fallbackId = useId()
    const id = idProp ?? fallbackId
    const [internalVisible, setInternalVisible] = useState(false)
    const isVisible = visible ?? internalVisible

    const toggleVisible = (): void => {
      const next = !isVisible
      if (onVisibleChange) onVisibleChange(next)
      else setInternalVisible(next)
    }

    const inputClass = ['input', size === 'lg' ? 'lg' : '', className ?? '']
      .filter(Boolean)
      .join(' ')

    return (
      <div className="input-group">
        <input
          ref={ref}
          id={id}
          type={isVisible ? 'text' : 'password'}
          className={inputClass}
          {...rest}
        />
        <div className="input-suffix">
          <button
            type="button"
            className="icon-btn sm"
            onClick={toggleVisible}
            aria-label={isVisible ? t('common.password.hide') : t('common.password.show')}
            aria-pressed={isVisible}
            tabIndex={-1}
          >
            <Icon name={isVisible ? 'eye-off' : 'eye'} aria-hidden="true" />
          </button>
          {extraAction}
        </div>
      </div>
    )
  },
)
